"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.classificationBandsQuerySchema = exports.classificationBandListSchema = exports.classificationBandSetSchema = exports.classificationBandSchema = exports.vitalDefinitionListSchema = exports.vitalDefinitionSchema = void 0;
/**
 * `RPM-003` — the shape `GET /api/rpm/v1/reference/vitals` returns (J-250).
 * `RPM-011` — the shape `GET /api/rpm/v1/reference/bands?setId=…` returns (J-253).
 *
 * THE BOUNDS LEAVE THE SERVER AS TEXT, NOT AS NUMBERS. `plausible_min` and `plausible_max` are
 * `numeric(8, 2)`, which drizzle-zod projects to `z.string()`, and that projection is kept rather
 * than coerced. `RPM-003` acceptance row 3 asks for "the same bounds the classifier used … byte for
 * byte": a bound parsed into an IEEE-754 double and printed again is a second rendering of the
 * value, and two renderings can differ. The column's own text is one rendering, so the route, the
 * classifier and the database all read the same bytes (`D-662`).
 *
 * No `config.phi` on the route that returns this: reference data is not patient data
 * (`01-RPM-SPEC.md:1649`).
 */
const drizzle_zod_1 = require("drizzle-zod");
const zod_1 = require("zod");
const rpm_config_js_1 = require("./db/rpm-config.js");
/** One definition row, as a client reads it. Attribution and period columns stay on the server. */
exports.vitalDefinitionSchema = (0, drizzle_zod_1.createSelectSchema)(rpm_config_js_1.vitalSignDefinitions)
    .pick({
    vitalCode: true,
    displayLabel: true,
    loincCode: true,
    loincCodeSecondary: true,
    ucumUnit: true,
    ucumExportUnit: true,
    plausibleMin: true,
    plausibleMax: true,
    isClassified: true,
    detectionPrecedence: true,
    detectionWordPatterns: true,
    detectionShapePatterns: true,
    labelNoisePattern: true,
    readingGroupKind: true,
    fhirComponentRole: true,
})
    .meta({
    id: "VitalDefinition",
    description: "One vital sign's LOINC codes, canonical units and plausibility bounds",
});
/** The whole reference set, in `detection_precedence` order — the order detection tries them in. */
exports.vitalDefinitionListSchema = zod_1.z
    .object({ items: zod_1.z.array(exports.vitalDefinitionSchema) })
    .meta({ id: "VitalDefinitionList" });
/**
 * One `classification_bands` row, as a client reads it (`RPM-011`).
 *
 * `boundaryValue` leaves the server as the `numeric` column's own text, for the reason `D-662` gives
 * for the plausibility bounds: a boundary parsed into an IEEE-754 double and printed again is a
 * second rendering, and two renderings can differ. The client renders bands; it never computes them,
 * which is the block's API row, so nothing here needs a number.
 *
 * `sourceVerbatim` and `sourceDefect` travel with the row on purpose. The one corrected cell — the
 * Concerning temperature upper bound, printed `Less than < 99 F` and stored `> 99` — is only
 * auditable at the surface if the surface is given what was printed (`CR-18`, `N16(h)`).
 */
exports.classificationBandSchema = (0, drizzle_zod_1.createSelectSchema)(rpm_config_js_1.classificationBands)
    .pick({
    classificationBandId: true,
    vitalCode: true,
    oxygenStatus: true,
    severityBand: true,
    direction: true,
    comparator: true,
    boundaryValue: true,
    unit: true,
    sourceCellRef: true,
    sourceDefect: true,
    sourceVerbatim: true,
})
    .meta({
    id: "ClassificationBand",
    description: "One protocol threshold cell: a vital, a direction, a strict comparator and a boundary",
});
/** The published set the rows belong to. What an alert pins, and what a surface names. */
exports.classificationBandSetSchema = (0, drizzle_zod_1.createSelectSchema)(rpm_config_js_1.classificationBandSets)
    .pick({
    classificationBandSetId: true,
    bandSetVersion: true,
    label: true,
    sourceDocumentRef: true,
})
    .extend({
    // ISO 8601 strings, not `z.date()`, which is the convention `patientDetailSchema` states and the
    // reason it gives: a response is JSON and JSON has no date, so `z.date()` gives the OpenAPI
    // document no type and the generated client reads `unknown | null`. The repository converts on
    // the way out, which is the one place that knows the value came from a `timestamp` column.
    effectiveFrom: zod_1.z.iso.datetime(),
    effectiveTo: zod_1.z.iso.datetime().nullable(),
})
    .meta({ id: "ClassificationBandSet", description: "One published, immutable band set" });
/**
 * One set and its rows. The set travels with them because a list of thresholds with no version is
 * the thing an old alert cannot be checked against.
 */
exports.classificationBandListSchema = zod_1.z
    .object({ set: exports.classificationBandSetSchema, items: zod_1.z.array(exports.classificationBandSchema) })
    .meta({ id: "ClassificationBandList" });
/**
 * The query of `GET /api/rpm/v1/reference/bands`.
 *
 * `setId` is REQUIRED, and that is the requirement rather than a preference: `RPM-011` says a
 * reading is scored against the set pinned to its workup, "never against the current set". A route
 * that defaulted to the current set when the parameter was absent would render today's thresholds
 * beside an old alert, which is the one reading the MUST forbids.
 */
// No `.meta({ id })`, for the reason `index.ts:167` states: an id makes the type provider emit a
// `$ref` to a component and `@fastify/swagger` expands parameters INLINE, so `app.swagger()` dies in
// `resolveLocalRef` with `Cannot read properties of undefined (reading 'type')`.
exports.classificationBandsQuerySchema = zod_1.z.object({ setId: zod_1.z.uuid() });
//# sourceMappingURL=rpm-reference.js.map